import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { AuthCheckbox } from '@/components/ui/auth-checkbox';
import { useAuth } from '@/utils/AuthContext';
import { API_URL } from '@/utils/config';
import { CheckCircle2 } from 'lucide-react';

interface ServiceFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const CONTACT_METHOD_KEYS = [
  { value: 'Email', key: 'serviceModal.contactMethods.email' },
  { value: 'Телефон', key: 'serviceModal.contactMethods.phone' },
  { value: 'Telegram', key: 'serviceModal.contactMethods.telegram' },
  { value: 'WhatsApp', key: 'serviceModal.contactMethods.whatsapp' },
] as const;

const SERVICE_OPTION_KEYS = [
  { value: 'Провести всю аналитику от анализа рынка до оффера', key: 'serviceModal.services.analytics' },
  { value: 'Спроектировать воронку продаж и финмодель', key: 'serviceModal.services.funnel' },
  { value: 'Создать MVP продукта', key: 'serviceModal.services.mvp' },
  { value: 'Создать тг бота', key: 'serviceModal.services.tgBot' },
  { value: 'Создать сайт', key: 'serviceModal.services.website' },
  { value: 'Привлечь трафик', key: 'serviceModal.services.traffic' },
] as const;

export function ServiceFormModal({ open, onOpenChange }: ServiceFormModalProps) {
  const { user } = useAuth();
  const { t } = useTranslation('subscription');

  const [formData, setFormData] = useState({
    contactMethod: '',
    name: user?.name || '',
    email: user?.email || '',
    phone: '',
    services: [] as string[],
    otherService: '',
    project: '',
  });
  const [showOther, setShowOther] = useState(false);

  const [errors, setErrors] = useState<{ name?: boolean; email?: boolean; project?: boolean }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);

  const resetForm = () => {
    setFormData({
      contactMethod: '',
      name: user?.name || '',
      email: user?.email || '',
      phone: '',
      services: [],
      otherService: '',
      project: '',
    });
    setShowOther(false);
    setErrors({});
    setSubmitError('');
    setIsSuccess(false);
  };

  const toggleService = (service: string) => {
    setFormData((prev) => ({
      ...prev,
      services: prev.services.includes(service)
        ? prev.services.filter((s) => s !== service)
        : [...prev.services, service],
    }));
  };

  const getContactFieldConfig = (method: string) => {
    switch (method) {
      case 'Телефон': return { labelKey: 'serviceModal.phoneLabel', placeholder: t('serviceModal.phonePlaceholder'), type: 'tel' };
      case 'Telegram': return { labelKey: 'serviceModal.telegramLabel', placeholder: t('serviceModal.telegramPlaceholder'), type: 'text' };
      case 'WhatsApp': return { labelKey: 'serviceModal.whatsappLabel', placeholder: t('serviceModal.whatsappPlaceholder'), type: 'tel' };
      default: return null;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const newErrors: { name?: boolean; email?: boolean; project?: boolean } = {};
    if (!formData.name.trim()) newErrors.name = true;
    if (!formData.email.trim()) newErrors.email = true;
    if (!formData.project.trim()) newErrors.project = true;

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsSubmitting(true);
    setSubmitError('');

    try {
      const response = await fetch(`${API_URL}/api/landing-form`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: 'service',
          name: formData.name.trim(),
          email: formData.email.trim(),
          phone: formData.phone.trim() || undefined,
          contactMethod: formData.contactMethod || undefined,
          services: formData.services.length > 0 ? formData.services : undefined,
          otherService: showOther && formData.otherService.trim() ? formData.otherService.trim() : undefined,
          project: formData.project.trim(),
        }),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => null);
        throw new Error(data?.error || t('serviceModal.sendError'));
      }

      setIsSuccess(true);
    } catch (error) {
      const msg = error instanceof Error ? error.message : t('serviceModal.unknownError');
      setSubmitError(t('serviceModal.errorPrefix', { error: msg }));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      resetForm();
    }
    onOpenChange(newOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-[580px] rounded-[24px] sm:rounded-[24px] border-none p-0 max-h-[90vh] flex flex-col overflow-hidden">
        <div className="overflow-y-auto p-8 flex-1 min-h-0" style={{ scrollbarWidth: 'thin', scrollbarColor: '#d1d5db transparent' }}>
        {isSuccess ? (
          <div className="flex flex-col items-center gap-4 py-8">
            <CheckCircle2 className="h-16 w-16 text-[#2197F1]" />
            <h2 className="text-[24px] font-bold text-center">{t('serviceModal.successTitle')}</h2>
            <p className="text-[14px] text-[#565858] text-center">
              {t('serviceModal.successDescription')}
            </p>
            <Button
              onClick={() => handleOpenChange(false)}
              className="mt-4 h-[48px] w-full rounded-[16px] bg-[#2197F1] hover:bg-[#1a7acc] text-white text-[16px] font-semibold"
            >
              {t('serviceModal.successClose')}
            </Button>
          </div>
        ) : (
          <>
            <DialogHeader className="text-center space-y-2">
              <DialogTitle className="text-[24px] font-bold text-center">
                {t('serviceModal.title')}
              </DialogTitle>
              <DialogDescription className="text-[14px] text-[#565858] text-center leading-relaxed">
                {t('serviceModal.description')}
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="mt-6 space-y-5">
              {submitError && (
                <div className="rounded-xl bg-red-50 p-3 text-[14px] text-red-600">
                  {submitError}
                </div>
              )}

              {/* Способ связи */}
              <div className="space-y-2">
                <Label className="text-[14px] text-[#565858]">
                  {t('serviceModal.contactMethodLabel')}
                </Label>
                <Select
                  value={formData.contactMethod}
                  onValueChange={(value) => setFormData({ ...formData, contactMethod: value, phone: '' })}
                  disabled={isSubmitting}
                >
                  <SelectTrigger className="text-[16px]">
                    <SelectValue placeholder={t('serviceModal.contactMethodPlaceholder')} />
                  </SelectTrigger>
                  <SelectContent>
                    {CONTACT_METHOD_KEYS.map((method) => (
                      <SelectItem key={method.value} value={method.value}>
                        {t(method.key)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Контактные данные (динамическое поле) */}
              {(() => {
                const config = getContactFieldConfig(formData.contactMethod);
                if (!config) return null;
                return (
                  <div className="space-y-2">
                    <Label htmlFor="sf-phone" className="text-[14px] text-[#565858]">
                      {t(config.labelKey)}
                    </Label>
                    <Input
                      id="sf-phone"
                      type={config.type}
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder={config.placeholder}
                      className="h-12 rounded-xl border-[#eeeeee] text-[16px]"
                      disabled={isSubmitting}
                    />
                  </div>
                );
              })()}

              {/* Имя */}
              <div className="space-y-2">
                <Label htmlFor="sf-name" className="text-[14px] text-[#565858]">
                  {t('serviceModal.nameLabel')}
                </Label>
                <Input
                  id="sf-name"
                  value={formData.name}
                  onChange={(e) => {
                    setFormData({ ...formData, name: e.target.value });
                    if (errors.name) setErrors({ ...errors, name: false });
                  }}
                  placeholder={t('serviceModal.namePlaceholder')}
                  className={`h-12 rounded-xl border-[#eeeeee] text-[16px] ${
                    errors.name ? 'border-red-500 focus:border-red-500' : ''
                  }`}
                  disabled={isSubmitting}
                />
              </div>

              {/* Email */}
              <div className="space-y-2">
                <Label htmlFor="sf-email" className="text-[14px] text-[#565858]">
                  {t('serviceModal.emailLabel')}
                </Label>
                <Input
                  id="sf-email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => {
                    setFormData({ ...formData, email: e.target.value });
                    if (errors.email) setErrors({ ...errors, email: false });
                  }}
                  placeholder={t('serviceModal.emailPlaceholder')}
                  className={`h-12 rounded-xl border-[#eeeeee] text-[16px] ${
                    errors.email ? 'border-red-500 focus:border-red-500' : ''
                  }`}
                  disabled={isSubmitting}
                />
              </div>

              {/* Услуги */}
              <div className="space-y-3">
                <Label className="text-[14px] text-[#565858]">
                  {t('serviceModal.servicesLabel')}
                </Label>
                {SERVICE_OPTION_KEYS.map((service) => (
                  <AuthCheckbox
                    key={service.value}
                    checked={formData.services.includes(service.value)}
                    onChange={() => toggleService(service.value)}
                    label={t(service.key)}
                    disabled={isSubmitting}
                  />
                ))}
                <AuthCheckbox
                  checked={showOther}
                  onChange={() => setShowOther(!showOther)}
                  label={t('serviceModal.services.other')}
                  disabled={isSubmitting}
                />
                {showOther && (
                  <Input
                    value={formData.otherService}
                    onChange={(e) => setFormData({ ...formData, otherService: e.target.value })}
                    placeholder={t('serviceModal.otherPlaceholder')}
                    className="h-12 rounded-xl border-[#eeeeee] text-[16px] ml-9"
                    disabled={isSubmitting}
                  />
                )}
              </div>

              {/* Описание проекта */}
              <div className="space-y-2">
                <Label htmlFor="sf-project" className="text-[14px] text-[#565858]">
                  {t('serviceModal.projectLabel')}
                </Label>
                <Textarea
                  id="sf-project"
                  value={formData.project}
                  onChange={(e) => {
                    setFormData({ ...formData, project: e.target.value });
                    if (errors.project) setErrors({ ...errors, project: false });
                  }}
                  placeholder={t('serviceModal.projectPlaceholder')}
                  rows={4}
                  className={`rounded-xl border-[#eeeeee] resize-none text-[16px] ${
                    errors.project ? 'border-red-500 focus:border-red-500' : ''
                  }`}
                  disabled={isSubmitting}
                />
              </div>

              {/* Кнопки */}
              <div className="flex gap-[24px]">
                <button
                  type="button"
                  onClick={() => handleOpenChange(false)}
                  disabled={isSubmitting}
                  className="flex-1 bg-transparent border border-[var(--grey-accent,#eeeeee)] border-solid text-[color:var(--black-text,#0d0d0d)] px-[24px] py-[12px] rounded-[16px] font-segoe font-semibold text-[16px] leading-[24px] hover:bg-[#2197f1] hover:text-white hover:border-[#2197f1] transition-colors text-center"
                >
                  {t('serviceModal.close')}
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1 bg-[#2197f1] text-white px-[24px] py-[12px] rounded-[16px] font-segoe font-semibold text-[16px] leading-[24px] hover:opacity-90 transition-opacity text-center"
                >
                  {isSubmitting ? t('serviceModal.submitting') : t('serviceModal.submit')}
                </button>
              </div>

              <p className="text-[12px] text-[#8c8c8c] text-center">
                {t('serviceModal.consent')}
              </p>
            </form>
          </>
        )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
